/**
 * 🚨 WiltedFriendsToast — global watcher that polls /api/farm/friends every
 * 60s and pops a sonner toast for each newly-spotted wilted plot belonging
 * to a friend. Throttled per-session-per-plot via sessionStorage.
 *
 * Mounted in App.js once. Runs only when the user is authenticated.
 */
import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 60_000;
const SEEN_KEY = "omni:farm-wilt-seen";

function getSeen() {
  try { return JSON.parse(sessionStorage.getItem(SEEN_KEY) || "{}"); }
  catch { return {}; }
}
function markSeen(plotIds) {
  const map = getSeen();
  for (const id of plotIds) map[id] = Date.now();
  sessionStorage.setItem(SEEN_KEY, JSON.stringify(map));
}

export default function WiltedFriendsToast() {
  const { user, loading } = useAuth();
  const timer = useRef(null);

  useEffect(() => {
    if (loading || !user) return;

    const check = async () => {
      try {
        const r = await axios.get(`${API}/farm/friends`);
        const farms = r.data?.farms || [];
        const seen = getSeen();
        const newlyWilted = [];
        for (const f of farms) {
          for (const p of (f.plots || [])) {
            if (p.status === "wilted" && !seen[p.plot_id]) {
              newlyWilted.push({ farm: f, plot: p });
            }
          }
        }
        if (newlyWilted.length === 0) return;
        // Single consolidated toast per poll so we don't blast the user
        const count = newlyWilted.length;
        const names = Array.from(new Set(newlyWilted.map(x => x.farm.name))).slice(0, 3).join(", ");
        const more = newlyWilted.length > 3 ? `, +${newlyWilted.length - 3} more` : "";
        toast.warning(
          count === 1
            ? `🚨 ${newlyWilted[0].farm.name}'s crop is wilting — revive it within 24h!`
            : `🚨 ${count} wilted crops from ${names}${more} — revive before they're gone!`,
          {
            duration: 8000,
            action: {
              label: "Open Farm",
              onClick: () => { window.location.href = "/farm"; },
            },
          }
        );
        markSeen(newlyWilted.map(x => x.plot.plot_id));
      } catch (e) {
        // silent — toast is optional decoration
      }
    };

    // Fire once after 5s, then every POLL_MS
    const initial = setTimeout(check, 5000);
    timer.current = setInterval(check, POLL_MS);
    return () => {
      clearTimeout(initial);
      if (timer.current) clearInterval(timer.current);
    };
  }, [user, loading]);

  return null;
}
